import { errorService } from '@/data/services/error/errorService'

/**
 * Standardized API response formats and normalization utilities
 * Centralizes all response normalization logic to eliminate duplication
 */

/**
 * Standard pagination metadata structure
 */
const DEFAULT_PAGINATION = {
  skip: 0,
  limit: 100,
  total: 0,
}

/**
 * Response type detection patterns
 */
const RESPONSE_PATTERNS = {
  // Standard paginated format
  PAGINATED: 'paginated',
  // Entity-specific wrapped format (e.g., {vehicles: [...], customers: [...]})
  ENTITY_WRAPPED: 'entity_wrapped',
  // Direct array response
  DIRECT_ARRAY: 'direct_array',
  // Single entity response
  SINGLE_ENTITY: 'single_entity',
  // Custom format (e.g., cycle data with special structure)
  CUSTOM: 'custom',
  // Invalid/empty response
  INVALID: 'invalid',
}

/**
 * Known entity wrapper keys from different API endpoints
 */
const ENTITY_WRAPPER_KEYS = [
  'vehicles',
  'customers',
  'sites',
  'controlStations',
  'control_stations',
  'cycles',
  'modes',
  'issues',
  'events',
  'data',
  'results',
]

/**
 * Detect the type of API response format
 * @param {any} response - Raw API response
 * @returns {string} Response pattern type
 */
function detectResponseType(response) {
  if (!response || (typeof response !== 'object' && !Array.isArray(response))) {
    return RESPONSE_PATTERNS.INVALID
  }

  // Check for standard paginated format
  if (response.items && Array.isArray(response.items)) {
    return RESPONSE_PATTERNS.PAGINATED
  }

  // Check for entity-wrapped format
  for (const key of ENTITY_WRAPPER_KEYS) {
    if (response[key] && Array.isArray(response[key])) {
      return RESPONSE_PATTERNS.ENTITY_WRAPPED
    }
  }

  // Check for direct array
  if (Array.isArray(response)) {
    return RESPONSE_PATTERNS.DIRECT_ARRAY
  }

  // Check for single entity (object with id or specific properties)
  if (typeof response === 'object' && (response.id || response.uuid || response.name)) {
    return RESPONSE_PATTERNS.SINGLE_ENTITY
  }

  // Check for custom format (has data but doesn't match other patterns)
  if (typeof response === 'object' && Object.keys(response).length > 0) {
    return RESPONSE_PATTERNS.CUSTOM
  }

  return RESPONSE_PATTERNS.INVALID
}

/**
 * Extract entity array from wrapped response
 * @param {Object} response - API response with entity wrapper
 * @returns {Array} Extracted entity array
 */
function extractEntityArray(response) {
  for (const key of ENTITY_WRAPPER_KEYS) {
    if (response[key] && Array.isArray(response[key])) {
      return response[key]
    }
  }
  return []
}

/**
 * Normalize API response to standard paginated format
 * @param {any} rawResponse - Raw API response
 * @param {Object} requestOptions - Original request options (for pagination context)
 * @returns {Object} Normalized response with consistent structure
 */
export function normalizeApiResponse(rawResponse, requestOptions = {}) {
  try {
    // Ensure requestOptions is an object (handle null/undefined)
    const safeRequestOptions =
      requestOptions && typeof requestOptions === 'object' ? requestOptions : {}
    const responseType = detectResponseType(rawResponse)

    switch (responseType) {
      case RESPONSE_PATTERNS.PAGINATED: {
        // Already in correct format, just ensure all fields exist
        const paginatedPagination = rawResponse.pagination
        return {
          items: rawResponse.items || [],
          total: paginatedPagination?.total ?? rawResponse.total ?? rawResponse.items?.length ?? 0,
          skip: paginatedPagination
            ? (paginatedPagination.page - 1) * paginatedPagination.page_size
            : (rawResponse.skip ?? safeRequestOptions.skip ?? DEFAULT_PAGINATION.skip),
          limit:
            paginatedPagination?.page_size ??
            rawResponse.limit ??
            safeRequestOptions.limit ??
            rawResponse.items?.length ??
            DEFAULT_PAGINATION.limit,
          ...rawResponse, // Preserve any additional metadata
        }
      }

      case RESPONSE_PATTERNS.ENTITY_WRAPPED: {
        // Extract array from entity wrapper
        const entityArray = extractEntityArray(rawResponse)
        const entityPagination = rawResponse.pagination
        return {
          items: entityArray,
          total: entityPagination?.total ?? rawResponse.total ?? entityArray.length,
          skip: entityPagination
            ? (entityPagination.page - 1) * entityPagination.page_size
            : (rawResponse.skip ?? safeRequestOptions.skip ?? DEFAULT_PAGINATION.skip),
          limit:
            entityPagination?.page_size ??
            rawResponse.limit ??
            safeRequestOptions.limit ??
            entityArray.length,
          // Preserve other metadata
          metadata: {
            ...rawResponse,
            // Remove the entity array key to avoid duplication
            ...Object.fromEntries(
              Object.entries(rawResponse).filter(([key]) => !ENTITY_WRAPPER_KEYS.includes(key))
            ),
          },
        }
      }

      case RESPONSE_PATTERNS.DIRECT_ARRAY:
        // Convert direct array to paginated format
        return {
          items: rawResponse,
          total: rawResponse.length,
          skip: safeRequestOptions.skip || DEFAULT_PAGINATION.skip,
          limit: safeRequestOptions.limit || rawResponse.length,
          metadata: {},
        }

      case RESPONSE_PATTERNS.SINGLE_ENTITY:
        // Single entity response - return as-is (not paginated)
        return rawResponse

      case RESPONSE_PATTERNS.CUSTOM:
        // Custom format - preserve structure but add metadata if possible
        return {
          ...rawResponse,
          // Add pagination metadata if this seems to be a list response
          ...(Array.isArray(Object.values(rawResponse)[0])
            ? {
                _normalized: true,
                _originalFormat: 'custom',
              }
            : {}),
        }
      default:
        // Fallback for invalid responses
        errorService.logError(new Error('Invalid API response format'), {
          responseType,
          response: rawResponse,
          context: 'API response normalization',
        })

        return {
          items: [],
          total: 0,
          skip: DEFAULT_PAGINATION.skip,
          limit: DEFAULT_PAGINATION.limit,
          metadata: { error: 'Invalid response format', originalResponse: rawResponse },
        }
    }
  } catch (error) {
    errorService.logError(error, {
      context: 'API response normalization failed',
      rawResponse,
      requestOptions,
    })

    // Emergency fallback
    return {
      items: [],
      total: 0,
      skip: DEFAULT_PAGINATION.skip,
      limit: DEFAULT_PAGINATION.limit,
      metadata: { error: 'Normalization failed', originalResponse: rawResponse },
    }
  }
}

/**
 * Normalize a single entity response (for detail endpoints)
 * @param {any} rawResponse - Raw API response
 * @param {string} entityType - Type of entity for error context
 * @returns {Object} Normalized single entity
 */
export function normalizeSingleEntityResponse(rawResponse, entityType = 'entity') {
  try {
    const responseType = detectResponseType(rawResponse)

    if (
      responseType === RESPONSE_PATTERNS.SINGLE_ENTITY ||
      responseType === RESPONSE_PATTERNS.CUSTOM
    ) {
      return rawResponse
    }

    // If it's an array with one item, extract it
    if (responseType === RESPONSE_PATTERNS.DIRECT_ARRAY && rawResponse.length === 1) {
      return rawResponse[0]
    }

    // If it's a paginated response with one item, extract it
    if (responseType === RESPONSE_PATTERNS.PAGINATED && rawResponse.items?.length === 1) {
      return rawResponse.items[0]
    }

    // If it's entity-wrapped with one item, extract it
    if (responseType === RESPONSE_PATTERNS.ENTITY_WRAPPED) {
      const entityArray = extractEntityArray(rawResponse)
      if (entityArray.length === 1) {
        return entityArray[0]
      }
    }

    // Fallback: log warning and return as-is
    errorService.logError(new Error(`Unexpected response format for single ${entityType}`), {
      responseType,
      response: rawResponse,
      context: `Single ${entityType} normalization`,
    })

    return rawResponse
  } catch (error) {
    errorService.logError(error, {
      context: `Single ${entityType} normalization failed`,
      rawResponse,
    })

    return rawResponse
  }
}

/**
 * Utility to check if a response is already normalized
 * @param {any} response - Response to check
 * @returns {boolean} True if response appears to be normalized
 */
function isNormalizedResponse(response) {
  return (
    response &&
    typeof response === 'object' &&
    Array.isArray(response.items) &&
    typeof response.total === 'number' &&
    typeof response.skip === 'number' &&
    typeof response.limit === 'number'
  )
}

/**
 * Migration helper to gradually migrate from old normalization to new
 * @param {any} response - Response that might be normalized by old logic
 * @returns {Object} Properly normalized response
 */
export function ensureNormalizedResponse(response) {
  if (isNormalizedResponse(response)) {
    return response
  }

  return normalizeApiResponse(response)
}
